import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { Users, Plus, Edit, Trash2, Search, Upload, CheckCircle, XCircle, Download, Filter, X, TrendingUp, UserCheck, UserX, FolderOpen, ChevronDown, ChevronUp, MessageSquare, Send, Eye, AlertCircle, Folder, Tag } from "lucide-react";
import Layout from "../components/Layout";
import PageHeader from "../components/PageHeader";
import ContactModal from "../components/ContactModal";
import { contactsAPI, groupsAPI } from "../services/api";
import axios from "axios";
import "../global.css";
import { useTheme } from "../contexts/ThemeContext";
import { PageSkeleton, SkeletonTabela } from '../components/Skeleton';

const Contacts = () => {
  const { darkMode } = useTheme();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [modalTab, setModalTab] = useState('dados');
  const [searchTerm, setSearchTerm] = useState("");
  const [errors, setErrors] = useState({});
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [importing, setImporting] = useState(false);
  const [consentimentoDeclarado, setConsentimentoDeclarado] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importTotal, setImportTotal] = useState(0);
  const [importResult, setImportResult] = useState(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [groups, setGroups] = useState([]);
  const [selectedGroupOption, setSelectedGroupOption] = useState('none');
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('nome_asc'); // nome_asc, nome_desc, data_asc, data_desc
  const [temperatureFilter, setTemperatureFilter] = useState('all');
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(50);
  const [totalContacts, setTotalContacts] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [contactDetails, setContactDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showCampaignMessagesModal, setShowCampaignMessagesModal] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  const [campaignMessages, setCampaignMessages] = useState([]);
  const [loadingCampaignMessages, setLoadingCampaignMessages] = useState(false);
  const [loadingMoreMessages, setLoadingMoreMessages] = useState(false);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const messagesContainerRef = React.useRef(null);
  
  // Função para formatar texto estilo WhatsApp
  const formatWhatsAppText = (text) => {
    if (!text) return '';
    
    return text
      // Negrito: *texto*
      .replace(/\*(.*?)\*/g, '<strong>$1</strong>')
      // Itálico: _texto_
      .replace(/_(.*?)_/g, '<em>$1</em>')
      // Tachado: ~texto~
      .replace(/~(.*?)~/g, '<del>$1</del>')
      // Monoespaçado: ```texto```
      .replace(/```(.*?)```/g, '<code class="bg-black bg-opacity-20 px-1 rounded">$1</code>')
      // Quebras de linha
      .replace(/\n/g, '<br/>');
  };
  
  const [formData, setFormData] = useState({
    nome: "", telefone: "", email: "", instagram: "", facebook: "",
    empresa: "", cargo: "", endereco: "", cpf_cnpj: "", status: true,
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    loadContacts();
    loadGroups();
  }, []);

  useEffect(() => {
    setCurrentPage(1);
    loadContacts(1, searchTerm, statusFilter, temperatureFilter);
  }, [statusFilter, temperatureFilter]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      loadContacts(1, searchTerm, statusFilter);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    loadContacts(currentPage, searchTerm, statusFilter);
  }, [currentPage]);

  useEffect(() => {
    if (showDetailsModal) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [showDetailsModal]);

  const loadContacts = async (page = currentPage, search = searchTerm, status = statusFilter, temp = temperatureFilter) => {
    try {
      const apiStatus = status === 'active' ? 'active' : status === 'inactive' ? 'inactive' : '';
      const response = await contactsAPI.list(user.cliente_id, page, itemsPerPage, search, apiStatus, temp !== 'all' ? temp : '');
      setContacts(response.data.messages || []);
      setTotalContacts(response.data.quantidade || 0);
      setTotalPages(response.data.totalPages || 1);
      if (response.data.stats) setStats(response.data.stats);
    } catch (error) {
      toast.error("Erro ao carregar contatos");
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await groupsAPI.list(user.cliente_id);
      setGroups(response.data?.grupos || []);
    } catch (error) {
      console.error("Erro ao carregar grupos:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({}); // limpa os erros antes de enviar

    try {
      const contactData = {
        ...formData,
        cliente_id: user.cliente_id,
        usuario_id: user.id,
      };

      if (editingContact) {
        await contactsAPI.update(user.cliente_id, editingContact.id, formData);
        toast.success("Contato atualizado com sucesso!");
      } else {
        await contactsAPI.create(contactData);
        toast.success("Contato criado com sucesso!");
      }

      setShowDetailsModal(false);
      setEditingContact(null);
      setFormData({ nome: "", telefone: "", email: "", instagram: "", facebook: "", empresa: "", cargo: "", endereco: "", cpf_cnpj: "", status: true });
      loadContacts();
    } catch (error) {
      if (error.response?.status === 400) {
        // supondo que o backend retorna algo como { message: "Telefone inválido" }
        setErrors((prev) => ({
          ...prev,
          telefone: "Utilize um telefone válido",
        }));
      } else {
        toast.error("Utilize um telefone válido" || "Erro ao salvar contato");
      }
    }
  };

  const handleEdit = (contact) => {
    setEditingContact(contact);
    setShowDetailsModal(true);
  };

  const handleDelete = async (id) => {
    setContactToDelete(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    try {
      await contactsAPI.delete(user.cliente_id, contactToDelete);
      toast.success("Contato excluído com sucesso!");
      loadContacts();
    } catch (error) {
      toast.error("Erro ao excluir contato");
    } finally {
      setShowDeleteModal(false);
      setContactToDelete(null);
    }
  };

  const downloadTemplate = () => {
    const csvContent = "nome,telefone,email,instagram,facebook,empresa,cargo,endereco,cpf_cnpj\nJoão Silva,11999999999,joao@email.com,@joaosilva,joao.silva,Empresa X,Gerente,Rua A 123,123.456.789-00\nMaria Santos,11888888888,,@maria,,,,,";
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'modelo_contatos.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImportCSV = async () => {
    if (!csvFile) {
      toast.error("Selecione um arquivo CSV");
      return;
    }

    // Validar grupo
    if (selectedGroupOption === 'existing' && !selectedGroupId) {
      toast.error("Selecione um grupo existente");
      return;
    }

    if (selectedGroupOption === 'new' && !newGroupName.trim()) {
      toast.error("Digite o nome do novo grupo");
      return;
    }

    setImporting(true);

    try {
      const text = await csvFile.text();
      const lines = text.split('\n').filter(line => line.trim());

      if (lines.length < 2) {
        toast.error("Arquivo CSV deve ter pelo menos um cabeçalho e uma linha de dados");
        setImporting(false);
        return;
      }

      // Detectar delimitador: vírgula (,) ou ponto e vírgula (;)
      const headerLine = lines[0];
      const commaSplit = headerLine.split(',');
      const semicolonSplit = headerLine.split(';');
      const delimiter = (semicolonSplit.length > commaSplit.length) ? ';' : ',';

      const headers = headerLine.split(delimiter).map(h => h.trim().toLowerCase());
      const nomeIndex = headers.findIndex(h => h.includes('nome'));
      const telefoneIndex = headers.findIndex(h => h.includes('telefone') || h.includes('phone'));

      if (nomeIndex === -1 || telefoneIndex === -1) {
        toast.error("CSV deve conter colunas 'nome' e 'telefone'");
        setImporting(false);
        return;
      }

      // Processar contatos do CSV com validação
      const contatos = [];
      const erros = [];
      const telefonesVistos = new Set();
      for (let i = 1; i < lines.length; i++) {
        const rawValues = lines[i].split(delimiter);
        const values = rawValues.map(v => v.trim().replace(/"/g, ''));

        if (values.length >= Math.max(nomeIndex + 1, telefoneIndex + 1)) {
          const nome = (values[nomeIndex] || `Contato ${i}`).trim();
          const telefoneBruto = (values[telefoneIndex] || '').trim();
          const telefone = telefoneBruto.replace(/\D/g, '');

          // Regras de validação no client
          if (!nome) {
            erros.push(`Linha ${i + 1}: nome vazio.`);
            continue;
          }
          if (!/^(?!0+$)\d{10,13}$/.test(telefone)) {
            erros.push(`Linha ${i + 1}: telefone inválido (${telefoneBruto}).`);
            continue;
          }
          if (telefonesVistos.has(telefone)) {
            erros.push(`Linha ${i + 1}: telefone duplicado no arquivo (${telefone}).`);
            continue;
          }

          telefonesVistos.add(telefone);
          const contato = { nome, telefone };
          const extraFields = ['email', 'instagram', 'facebook', 'empresa', 'cargo', 'endereco', 'cpf_cnpj'];
          extraFields.forEach(field => {
            const idx = headers.findIndex(h => h.includes(field));
            if (idx !== -1 && values[idx]?.trim()) contato[field] = values[idx].trim();
          });
          contatos.push(contato);
        }
      }

      if (erros.length) {
        const preview = erros.slice(0, 5).join('\n');
        toast.error(`Erros no CSV:\n${preview}${erros.length > 5 ? `\n...e mais ${erros.length - 5}` : ''}`);
        setImporting(false);
        return;
      }

      // Preparar dados para envio
      const importData = {
        contatos,
        consentimento_declarado: consentimentoDeclarado,
      };

      // Adicionar informações do grupo conforme seleção
      if (selectedGroupOption === 'existing') {
        importData.grupoId = selectedGroupId;
      } else if (selectedGroupOption === 'new') {
        importData.novoGrupoNome = newGroupName;
        if (newGroupDescription.trim()) {
          importData.novoGrupoDescricao = newGroupDescription;
        }
      }

      // Fazer importação via API
      const response = await contactsAPI.importCSV(importData);

      setImportResult({
        imported: response.data.importados,
        errors: response.data.erros,
        total: response.data.total,
        errorDetails: response.data.detalhesErros || [],
        grupoNome: response.data.grupoNome
      });

      setImporting(false);
      setShowImportModal(false);
      setShowResultModal(true);
      setCsvFile(null);
      setSelectedGroupOption('none');
      setSelectedGroupId('');
      setNewGroupName('');
      setNewGroupDescription('');
      loadContacts();
      loadGroups(); // Recarregar grupos caso tenha criado um novo
    } catch (error) {
      toast.error(error.response?.data?.message || "Erro ao processar arquivo CSV");
      setImporting(false);
    }
  };

  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0, recentlyAdded: 0 });

  const filteredContacts = contacts;
  const sortedContacts = contacts;
  const paginatedContacts = contacts;

  // Função para pegar primeira letra sem emoji
  const getInitial = (name) => {
    if (!name) return '?';
    const normalized = name
      .replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '')
      .replace(/[^\w\s]/g, '')
      .trim();
    return normalized.charAt(0)?.toUpperCase() || '?';
  };

  // Ordenar contatos
  const normalizeForSort = (str) => {
    return (str || '')
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove acentos
      .replace(/[\p{Emoji_Presentation}\p{Extended_Pictographic}]/gu, '') // Remove TODOS os emojis
      .replace(/[^\w\s]/g, '') // Remove outros caracteres especiais
      .trim()
      .toLowerCase();
  };

  const toggleSelectAll = () => {
    if (selectedContacts.length === paginatedContacts.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(paginatedContacts.map(c => c.id));
    }
  };

  const toggleSelectContact = (id) => {
    setSelectedContacts(prev => 
      prev.includes(id) ? prev.filter(cId => cId !== id) : [...prev, id]
    );
  };

  const handleBulkDelete = async () => {
    if (selectedContacts.length === 0) return;
    try {
      await Promise.all(selectedContacts.map(id => contactsAPI.delete(user.cliente_id, id)));
      toast.success(`${selectedContacts.length} contato(s) excluído(s)`);
      setSelectedContacts([]);
      loadContacts();
    } catch (error) {
      toast.error("Erro ao excluir contatos");
    }
  };

  const exportContacts = () => {
    const csv = ['nome,telefone,status', ...filteredContacts.map(c => `${c.nome},${c.telefone},${c.status ? 'Ativo' : 'Inativo'}`)].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'contatos.csv';
    link.click();
  };

  const handleExpandContact = async (contact) => {
    setEditingContact(contact);
    setShowDetailsModal(true);
  };

  const handleViewCampaignMessages = async (campanha, contatoId) => {
    setSelectedCampaign(campanha);
    setShowCampaignMessagesModal(true);
    setLoadingCampaignMessages(true);
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/registro-mensagens/campanha/${campanha.id}/contato/${contatoId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCampaignMessages(response.data.messages || []);
      setHasMoreMessages(response.data.hasMore || false);
      
      // Scroll para a primeira mensagem da campanha após carregar
      setTimeout(() => {
        const firstCampaignMsg = document.getElementById('first-campaign-message');
        if (firstCampaignMsg) {
          firstCampaignMsg.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);
    } catch (error) {
      toast.error("Erro ao carregar mensagens da campanha");
      setShowCampaignMessagesModal(false);
    } finally {
      setLoadingCampaignMessages(false);
    }
  };

  const loadMoreMessages = async () => {
    if (loadingMoreMessages || !hasMoreMessages || !selectedCampaign || !contactDetails) return;
    
    setLoadingMoreMessages(true);
    const oldestMessage = campaignMessages[0];
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/registro-mensagens/campanha/${selectedCampaign.id}/contato/${contactDetails.id}?before=${oldestMessage.create_at}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const newMessages = response.data.messages || [];
      setCampaignMessages(prev => [...newMessages, ...prev]);
      setHasMoreMessages(response.data.hasMore || false);
    } catch (error) {
      toast.error("Erro ao carregar mais mensagens");
    } finally {
      setLoadingMoreMessages(false);
    }
  };

  const handleScroll = (e) => {
    if (e.target.scrollTop === 0 && hasMoreMessages && !loadingMoreMessages) {
      const scrollHeight = e.target.scrollHeight;
      loadMoreMessages().then(() => {
        // Manter posição de scroll
        setTimeout(() => {
          e.target.scrollTop = e.target.scrollHeight - scrollHeight;
        }, 0);
      });
    }
  };
  if (loading) {
    return (
      <Layout>
        <PageSkeleton title="Contatos" subtitle="Gerencie e visualize insights dos seus contatos">
          <SkeletonTabela colunas={7} linhas={10} />
        </PageSkeleton>
      </Layout>
    );
  }


  return (
    <Layout>
      <div className="space-y-3 sm:space-y-6">
        <PageHeader title="Contatos" subtitle="Gerencie e visualize insights dos seus contatos">
            {selectedContacts.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="secondary-button inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Excluir ({selectedContacts.length})</span>
              </button>
            )}
            <button
              onClick={exportContacts}
              className="secondary-button inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md text-white"
            >
              <Download className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Exportar</span>
            </button>
            <button
              onClick={() => setShowImportModal(true)}
              className="secondary-button inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md text-white"
            >
              <Upload className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Importar</span>
            </button>
            <button
              onClick={() => {
                setEditingContact(null);
                setContactDetails(null);
                setFormData({ nome: "", telefone: "", email: "", instagram: "", facebook: "", empresa: "", cargo: "", endereco: "", cpf_cnpj: "", status: true });
                setModalTab('dados');
                setShowDetailsModal(true);
              }}
              className="gradient-button inline-flex items-center px-2 py-1.5 sm:px-4 sm:py-2 border border-transparent text-sm font-medium rounded-md text-white bg-nomoz-orange hover:bg-nomoz-orange-dark"
            >
              <Plus className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Novo</span>
            </button>
        </PageHeader>

        {/* Stats Dashboard */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-4">
          <div className={`rounded-lg p-3 sm:p-6 ${darkMode ? "bg-zinc-800 border border-gray-700" : "bg-white border border-gray-200"}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs sm:text-sm font-medium ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Total</p>
                <p className={`text-xl sm:text-3xl font-bold mt-1 sm:mt-2 ${darkMode ? "text-white" : "text-gray-900"}`}>{stats.total}</p>
              </div>
              <div className="h-8 w-8 sm:h-12 sm:w-12 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                <Users className="h-4 w-4 sm:h-6 sm:w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className={`rounded-lg p-3 sm:p-6 ${darkMode ? "bg-zinc-800 border border-gray-700" : "bg-white border border-gray-200"}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs sm:text-sm font-medium ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Ativos</p>
                <p className={`text-xl sm:text-3xl font-bold mt-1 sm:mt-2 ${darkMode ? "text-white" : "text-gray-900"}`}>{stats.active}</p>
              </div>
              <div className="h-8 w-8 sm:h-12 sm:w-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                <UserCheck className="h-4 w-4 sm:h-6 sm:w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className={`rounded-lg p-3 sm:p-6 ${darkMode ? "bg-zinc-800 border border-gray-700" : "bg-white border border-gray-200"}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs sm:text-sm font-medium ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Inativos</p>
                <p className={`text-xl sm:text-3xl font-bold mt-1 sm:mt-2 ${darkMode ? "text-white" : "text-gray-900"}`}>{stats.inactive}</p>
              </div>
              <div className="h-8 w-8 sm:h-12 sm:w-12 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <UserX className="h-4 w-4 sm:h-6 sm:w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className={`rounded-lg p-3 sm:p-6 ${darkMode ? "bg-zinc-800 border border-gray-700" : "bg-white border border-gray-200"}`}>
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-xs sm:text-sm font-medium ${darkMode ? "text-gray-400" : "text-gray-600"}`}>Últimos 7 dias</p>
                <p className={`text-xl sm:text-3xl font-bold mt-1 sm:mt-2 ${darkMode ? "text-white" : "text-gray-900"}`}>{stats.recentlyAdded}</p>
              </div>
              <div className="h-8 w-8 sm:h-12 sm:w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                <TrendingUp className="h-4 w-4 sm:h-6 sm:w-6 text-purple-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-4">
          <div className="flex-1 relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Buscar por nome ou telefone..."
              className={`block w-full rounded-md shadow-sm py-2 px-3 pl-10 ${
                darkMode
                  ? "bg-zinc-700 border border-gray-600 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange text-white placeholder-gray-400"
                  : "bg-white border border-gray-300 focus:outline-none focus:ring-nomoz-orange focus:border-nomoz-orange text-gray-900 placeholder-gray-500"
              }`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="flex gap-1.5 sm:gap-2 overflow-x-auto flex-wrap pb-1 sm:pb-0">
            <button
              onClick={() => { setStatusFilter('all'); setTemperatureFilter('all'); }}
              className={`px-2 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                statusFilter === 'all' && temperatureFilter === 'all'
                  ? 'bg-nomoz-navy text-white'
                  : darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setStatusFilter(statusFilter === 'active' ? 'all' : 'active')}
              className={`px-2 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                statusFilter === 'active'
                  ? 'bg-nomoz-navy text-white'
                  : darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Ativos
            </button>
            <button
              onClick={() => setStatusFilter(statusFilter === 'inactive' ? 'all' : 'inactive')}
              className={`px-2 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                statusFilter === 'inactive'
                  ? 'bg-nomoz-navy text-white'
                  : darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
            >
              Inativos
            </button>

            <div className={`hidden sm:block h-6 w-px ${darkMode ? 'bg-zinc-600' : 'bg-gray-300'}`} />

            {[
              { value: 'quente', label: '🔥 Quente' },
              { value: 'morno', label: '🟡 Morno' },
              { value: 'frio', label: '🧊 Frio' },
            ].map(t => (
              <button
                key={t.value}
                onClick={() => setTemperatureFilter(temperatureFilter === t.value ? 'all' : t.value)}
                className={`px-2 py-1.5 sm:px-3 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                  temperatureFilter === t.value
                    ? t.value === 'quente' ? 'bg-red-600 text-white'
                    : t.value === 'morno' ? 'bg-yellow-600 text-white'
                    : t.value === 'frio' ? 'bg-blue-600 text-white'
                    : 'bg-nomoz-navy text-white'
                    : darkMode ? 'bg-zinc-700 text-gray-300 hover:bg-zinc-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                {t.label}
              </button>
            ))}
            
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className={`px-2 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium border flex-shrink-0 ${
                darkMode 
                  ? 'bg-zinc-700 text-gray-300 border-zinc-600 hover:bg-zinc-600' 
                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
              }`}
            >
              <option value="nome_asc">Nome (A-Z)</option>
              <option value="nome_desc">Nome (Z-A)</option>
              <option value="data_desc">Mais recentes</option>
              <option value="data_asc">Mais antigos</option>
              <option value="score_desc">Maior score</option>
              <option value="score_asc">Menor score</option>
            </select>

            <button
              onClick={() => {
                setSearchTerm('');
                setStatusFilter('all');
                setTemperatureFilter('all');
                setSortBy('nome_asc');
              }}
              disabled={!searchTerm && statusFilter === 'all' && temperatureFilter === 'all' && sortBy === 'nome_asc'}
              className={`px-2 py-1.5 sm:px-4 sm:py-2 rounded-md text-xs sm:text-sm font-medium transition-colors whitespace-nowrap flex-shrink-0 ${
                searchTerm || statusFilter !== 'all' || temperatureFilter !== 'all' || sortBy !== 'nome_asc'
                  ? darkMode 
                    ? 'bg-red-600 text-white hover:bg-red-700' 
                    : 'bg-red-500 text-white hover:bg-red-600'
                  : darkMode
                    ? 'bg-zinc-700 text-gray-500 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              Limpar Filtros
            </button>
          </div>
        </div>

        {/* Results Info */}
        <div className="flex items-center justify-between">
          <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
            {totalContacts} contato(s) encontrado(s)
          </p>
          {selectedContacts.length > 0 && (
            <button
              onClick={() => setSelectedContacts([])}
              className={`text-sm flex items-center gap-1 ${darkMode ? "text-gray-400 hover:text-white" : "text-gray-600 hover:text-gray-900"}`}
            >
              <X className="h-4 w-4" />
              Limpar seleção
            </button>
          )}
        </div>

        {/* Contacts Table (desktop) / Cards (mobile) */}
        {paginatedContacts.length === 0 ? (
          <div className={`rounded-lg p-12 text-center ${darkMode ? "bg-zinc-800 border border-gray-700" : "bg-white border border-gray-200"}`}>
            <Users className={`mx-auto h-12 w-12 mb-4 ${darkMode ? "text-gray-400" : "text-gray-400"}`} />
            <h3 className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
              Nenhum contato encontrado
            </h3>
            <p className={`text-sm mt-1 ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
              {searchTerm || statusFilter !== 'all' ? "Tente ajustar os filtros" : "Comece adicionando um novo contato"}
            </p>
          </div>
        ) : (
          <>
            {/* Desktop: Tabela */}
            <div className={`hidden md:block rounded-xl border overflow-hidden ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`}>
              <table className="w-full">
                <thead className={darkMode ? 'bg-zinc-900' : 'bg-gray-50'}>
                  <tr>
                    <th className="w-10 px-4 py-3"><input type="checkbox" onChange={(e) => e.target.checked ? setSelectedContacts(paginatedContacts.map(c => c.id)) : setSelectedContacts([])} checked={selectedContacts.length === paginatedContacts.length && paginatedContacts.length > 0} className="h-4 w-4 rounded border-gray-300" /></th>
                    <th className={`text-left px-4 py-3 text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Contato</th>
                    <th className={`text-left px-4 py-3 text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Telefone</th>
                    <th className={`text-left px-4 py-3 text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Lead</th>
                    <th className={`text-left px-4 py-3 text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Status</th>
                    <th className={`text-left px-4 py-3 text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Opt-in</th>
                    <th className={`text-right px-4 py-3 text-xs font-medium uppercase tracking-wider ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>Ações</th>
                  </tr>
                </thead>
                <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-100'}`}>
                  {paginatedContacts.map((contact) => (
                    <tr key={contact.id} className={`transition-colors ${darkMode ? 'hover:bg-zinc-700/50' : 'hover:bg-gray-50'}`}>
                      <td className="px-4 py-3"><input type="checkbox" checked={selectedContacts.includes(contact.id)} onChange={() => toggleSelectContact(contact.id)} className="h-4 w-4 rounded border-gray-300" /></td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="h-9 w-9 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">{getInitial(contact.nome)}</div>
                          <span className={`font-medium truncate max-w-[200px] ${darkMode ? 'text-white' : 'text-gray-900'}`}>{contact.nome || "Sem nome"}</span>
                        </div>
                      </td>
                      <td className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>{contact.telefone || "—"}</td>
                      <td className="px-4 py-3">
                        {contact.lead_temperature ? (
                          <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${contact.lead_temperature === 'quente' ? 'bg-red-100 text-red-800' : contact.lead_temperature === 'morno' ? 'bg-yellow-100 text-yellow-800' : 'bg-blue-100 text-blue-800'}`}>
                            {contact.lead_temperature === 'quente' ? '🔥' : contact.lead_temperature === 'morno' ? '🟡' : '🧊'}
                          </span>
                        ) : <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>—</span>}
                      </td>
                      <td className="px-4 py-3"><span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${contact.status ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>{contact.status ? "Ativo" : "Inativo"}</span></td>
                      <td className="px-4 py-3">
                        <button onClick={async () => { const novo = !contact.opt_in_ativo; try { await contactsAPI.toggleOptIn(contact.id, novo); contact.opt_in_ativo = novo; setContacts([...contacts]); } catch { toast.error('Erro ao alterar opt-in'); } }} className={`${contact.opt_in_ativo ? 'text-emerald-500' : darkMode ? 'text-gray-500' : 'text-gray-400'}`} title={contact.opt_in_ativo ? 'Opt-in ativo' : 'Sem opt-in'}>
                          {contact.opt_in_ativo ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                        </button>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button onClick={() => handleExpandContact(contact)} className={`p-1.5 rounded-md transition-colors ${darkMode ? 'hover:bg-zinc-600 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`} title="Ver Detalhes"><Eye className="h-4 w-4" /></button>
                          <button onClick={() => handleEdit(contact)} className={`p-1.5 rounded-md transition-colors ${darkMode ? 'hover:bg-zinc-600 text-gray-400 hover:text-white' : 'hover:bg-gray-100 text-gray-500 hover:text-gray-900'}`} title="Editar"><Edit className="h-4 w-4" /></button>
                          <button onClick={() => { setContactToDelete(contact); setShowDeleteModal(true); }} className="p-1.5 rounded-md transition-colors text-red-400 hover:text-red-600 hover:bg-red-50" title="Excluir"><Trash2 className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Mobile: Cards */}
            <div className="md:hidden grid grid-cols-1 gap-2">
          {paginatedContacts.map((contact) => (
              <div
                key={contact.id}
                className={`rounded-lg p-3 sm:p-5 transition-all hover:shadow-lg ${
                  selectedContacts.includes(contact.id)
                    ? darkMode ? "bg-green-900/30 border-2 border-nomoz-orange" : "bg-green-50 border-2 border-nomoz-orange"
                    : darkMode ? "bg-zinc-800 border border-gray-700 hover:border-gray-600" : "bg-white border border-gray-200 hover:border-gray-300"
                }`}
              >
                <div className="flex items-start justify-between mb-2 sm:mb-4">
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedContacts.includes(contact.id)}
                      onChange={() => toggleSelectContact(contact.id)}
                      className="h-4 w-4 text-green-600 focus:ring-nomoz-orange border-gray-300 rounded"
                    />
                    <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-base sm:text-lg flex-shrink-0">
                      {getInitial(contact.nome)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {contact.lead_temperature && (
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        contact.lead_temperature === 'quente' ? 'bg-red-100 text-red-800' :
                        contact.lead_temperature === 'morno' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-blue-100 text-blue-800'
                      }`} title={`Lead score: ${contact.lead_score || 0}/100`}>
                        {contact.lead_temperature === 'quente' ? '🔥 Quente' : contact.lead_temperature === 'morno' ? '🟡 Morno' : '🧊 Frio'}
                      </span>
                    )}
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        contact.status
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {contact.status ? "Ativo" : "Inativo"}
                    </span>
                  </div>
                </div>

                <div className="mb-2 sm:mb-3 min-w-0">
                  <h3 className={`text-base sm:text-lg font-semibold mb-1 truncate ${darkMode ? "text-white" : "text-gray-900"}`}>
                    {contact.nome || "Sem nome"}
                    {contact.opt_in_ativo && <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded-full bg-emerald-500/10 text-emerald-500 font-medium">opt-in</span>}
                  </h3>
                  <p className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                    {contact.telefone || "Sem telefone"}
                  </p>
                </div>

                {contact.total_enviadas > 0 && (
                  <div className="mb-2 sm:mb-3">
                    <div className="flex items-center justify-between mb-1">
                      <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                        📩 Entregues: {contact.total_entregues || 0} de {contact.total_enviadas}
                      </span>
                      {contact.total_lidas > 0 && (
                        <span className={`text-xs ${darkMode ? "text-gray-400" : "text-gray-500"}`}>
                          👁 Lidas: {contact.total_lidas}
                        </span>
                      )}
                    </div>
                    <div className={`w-full h-1.5 rounded-full ${darkMode ? "bg-zinc-700" : "bg-gray-200"}`}>
                      <div
                        className={`h-1.5 rounded-full ${
                          (contact.taxa_entrega || 0) >= 80 ? 'bg-green-500' :
                          (contact.taxa_entrega || 0) >= 40 ? 'bg-yellow-500' : 'bg-red-500'
                        }`}
                        style={{ width: `${Math.min(100, contact.taxa_entrega || 0)}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className={`flex items-center justify-between gap-2 pt-2 sm:pt-3 border-t ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <button
                    onClick={() => handleExpandContact(contact)}
                    className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm transition-colors ${
                      darkMode
                        ? "text-gray-300 hover:text-white hover:bg-zinc-700"
                        : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                    }`}
                  >
                    <Eye className="h-4 w-4 sm:mr-1" />
                    <span className="hidden sm:inline">Ver Detalhes</span>
                  </button>
                  <div className="flex gap-2">
                    <button
                      onClick={async () => {
                        const novo = !contact.opt_in_ativo;
                        try {
                          await contactsAPI.toggleOptIn(contact.id, novo);
                          contact.opt_in_ativo = novo;
                          setContacts([...contacts]);
                        } catch { toast.error('Erro ao alterar opt-in'); }
                      }}
                      className={`p-2 rounded-md transition-colors ${contact.opt_in_ativo
                        ? 'text-emerald-500 hover:text-emerald-700 hover:bg-emerald-50'
                        : darkMode ? 'text-gray-500 hover:text-gray-300 hover:bg-zinc-700' : 'text-gray-400 hover:text-gray-600 hover:bg-gray-100'
                      }`}
                      title={contact.opt_in_ativo ? 'Opt-in ativo (clique para desativar)' : 'Sem opt-in (clique para ativar)'}
                    >
                      {contact.opt_in_ativo ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                    </button>
                    <button
                      onClick={() => handleEdit(contact)}
                      className={`p-2 rounded-md transition-colors ${
                        darkMode
                          ? "text-gray-300 hover:text-white hover:bg-zinc-700"
                          : "text-gray-600 hover:text-gray-900 hover:bg-gray-100"
                      }`}
                      title="Editar"
                    >
                      <Edit className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(contact.id)}
                      className="p-2 rounded-md text-red-600 hover:text-red-800 hover:bg-red-50 transition-colors"
                      title="Excluir"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
            </div>
          </>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className={`rounded-lg p-4 ${darkMode ? "bg-zinc-800 border border-gray-700" : "bg-white border border-gray-200"}`}>
            <div className="flex flex-col sm:flex-row items-center justify-between gap-2">
              <div className={`text-sm ${darkMode ? "text-gray-400" : "text-gray-700"}`}>
                Mostrando {((currentPage - 1) * itemsPerPage) + 1} a {Math.min(currentPage * itemsPerPage, totalContacts)} de {totalContacts}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    currentPage === 1
                      ? darkMode ? 'bg-zinc-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : darkMode ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  Anterior
                </button>
                <span className={`px-3 py-1 text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                  Página {currentPage} de {totalPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    currentPage === totalPages
                      ? darkMode ? 'bg-zinc-700 text-gray-500 cursor-not-allowed' : 'bg-gray-200 text-gray-400 cursor-not-allowed'
                      : darkMode ? 'bg-zinc-700 text-white hover:bg-zinc-600' : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-300'
                  }`}
                >
                  Próxima
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Contact Modal */}
        {showDetailsModal && (
          <ContactModal
            contact={editingContact}
            isNew={!editingContact}
            darkMode={darkMode}
            user={user}
            onClose={() => { setShowDetailsModal(false); setEditingContact(null); }}
            onSave={() => loadContacts()}
          />
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteModal && (
          <div
            className="modal-overlay"
            onClick={() => setShowDeleteModal(false)}
          >
            <div
              className={`modal-container max-w-md ${darkMode ? "bg-zinc-800" : "bg-white"}`}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="modal-header">
                <h3 className={`modal-title ${darkMode ? "text-red-400" : "text-red-600"}`}>
                  Confirmar Exclusão
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setContactToDelete(null);
                  }}
                  className={`modal-close-button ${darkMode ? "text-white" : "text-gray-900"}`}
                >
                  ×
                </button>
              </div>

              {/* Body */}
              <div className="modal-body">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex-shrink-0">
                    <svg
                      className="w-10 h-10 text-red-400"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h4 className={`text-lg font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                      Tem certeza que deseja excluir este contato?
                    </h4>
                    <p className={`text-sm mt-1 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                      Esta ação não pode ser desfeita.
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => {
                    setShowDeleteModal(false);
                    setContactToDelete(null);
                  }}
                  className={`modal-button modal-button-secondary ${
                    darkMode
                      ? "border-gray-600 text-white hover:bg-zinc-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={confirmDelete}
                  className="modal-button bg-red-600 hover:bg-red-700 text-white"
                >
                  Excluir
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import Progress Modal */}
        {importing && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
            <div className={`rounded-lg p-8 max-w-md w-full mx-4 ${darkMode ? "bg-zinc-800" : "bg-white"}`}>
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-nomoz-orange mx-auto mb-4"></div>
                <h3 className={`text-lg font-medium mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}>
                  Importando Contatos...
                </h3>
                <p className={`text-sm mb-4 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                  Não saia desta página durante a importação
                </p>

                <div className={`w-full bg-gray-200 rounded-full h-2 mb-2 ${darkMode ? "bg-gray-700" : "bg-gray-200"}`}>
                  <div
                    className="bg-nomoz-navy h-2 rounded-full transition-all duration-300"
                    style={{ width: `${importTotal > 0 ? (importProgress / importTotal) * 100 : 0}%` }}
                  ></div>
                </div>

                <p className={`text-sm ${darkMode ? "text-gray-300" : "text-gray-700"}`}>
                  {importProgress} de {importTotal} contatos processados
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Import Result Modal */}
        {showResultModal && importResult && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center z-50">
            <div className={`rounded-lg p-6 max-w-lg w-full mx-4 ${darkMode ? "bg-zinc-800" : "bg-white"}`}>
              <div className="text-center mb-6">
                <div className={`mx-auto flex items-center justify-center h-16 w-16 rounded-full mb-4 ${
                  importResult.imported > 0 ? "bg-green-100" : "bg-red-100"
                }`}>
                  {importResult.imported > 0 ? (
                    <CheckCircle className="h-8 w-8 text-green-600" />
                  ) : (
                    <XCircle className="h-8 w-8 text-red-600" />
                  )}
                </div>

                <h3 className={`text-lg font-medium mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}>
                  Importação Concluída!
                </h3>
              </div>

              <div className={`space-y-3 mb-6 p-4 rounded-lg ${darkMode ? "bg-zinc-700" : "bg-gray-50"}`}>
                <div className="flex justify-between">
                  <span className={darkMode ? "text-gray-300" : "text-gray-600"}>Total processados:</span>
                  <span className={`font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>{importResult.total}</span>
                </div>
                <div className="flex justify-between">
                  <span className={darkMode ? "text-gray-300" : "text-gray-600"}>Importados com sucesso:</span>
                  <span className="font-medium text-green-600">{importResult.imported}</span>
                </div>
                <div className="flex justify-between">
                  <span className={darkMode ? "text-gray-300" : "text-gray-600"}>Erros:</span>
                  <span className="font-medium text-red-600">{importResult.errors}</span>
                </div>
                {importResult.grupoNome && (
                  <div className="flex justify-between pt-2 border-t border-gray-600">
                    <span className={darkMode ? "text-gray-300" : "text-gray-600"}>Associados ao grupo:</span>
                    <span className={`font-medium ${darkMode ? "text-green-400" : "text-green-600"}`}>{importResult.grupoNome}</span>
                  </div>
                )}
              </div>

              {importResult.errors > 0 && importResult.errorDetails.length > 0 && (
                <div className={`mb-6 p-4 rounded-lg ${darkMode ? "bg-red-900/20" : "bg-red-50"}`}>
                  <h4 className={`text-sm font-medium mb-2 ${darkMode ? "text-red-400" : "text-red-800"}`}>
                    Primeiros erros encontrados:
                  </h4>
                  <div className={`text-xs space-y-1 max-h-32 overflow-y-auto ${darkMode ? "text-red-300" : "text-red-700"}`}>
                    {importResult.errorDetails.map((error, index) => (
                      <div key={index}>• {error}</div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex justify-center">
                <button
                  onClick={() => {
                    setShowResultModal(false);
                    setImportResult(null);
                    setImportProgress(0);
                    setImportTotal(0);
                  }}
                  className="gradient-button px-6 py-2 rounded-md text-white font-medium"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Import CSV Modal */}
        {showImportModal && (
          <div className="modal-overlay" onClick={() => {
            setShowImportModal(false);
            setCsvFile(null);
            setSelectedGroupOption('none');
            setSelectedGroupId('');
            setNewGroupName('');
            setNewGroupDescription('');
          }}>
            <div
              className={`modal-container max-w-md ${darkMode ? "bg-zinc-800" : "bg-white"}`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h3 className={`modal-title ${darkMode ? "text-white" : "text-gray-900"}`}>
                  Importar Contatos CSV
                </h3>
                <button
                  type="button"
                  onClick={() => {
                    setShowImportModal(false);
                    setCsvFile(null);
                    setSelectedGroupOption('none');
                    setSelectedGroupId('');
                    setNewGroupName('');
                    setNewGroupDescription('');
                  }}
                  className={`modal-close-button ${darkMode ? "text-white" : "text-gray-900"}`}
                >
                  ×
                </button>
              </div>

              <div className="modal-body space-y-4">
                <div>
                  <label className={`block text-sm font-medium mb-2 ${darkMode ? "text-white" : "text-gray-900"}`}>
                    Arquivo CSV
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setCsvFile(e.target.files[0])}
                    className={`block w-full text-sm rounded-md border py-2 px-3 ${
                      darkMode
                        ? "border-gray-600 bg-zinc-700 text-white file:bg-zinc-600 file:text-white"
                        : "border-gray-300 bg-white text-gray-900 file:bg-gray-50 file:text-gray-700"
                    } file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-medium hover:file:bg-gray-100`}
                  />
                </div>

                {/* Opções de Grupo */}
                <div className={`border-t pt-4 ${darkMode ? "border-gray-700" : "border-gray-200"}`}>
                  <label className={`block text-sm font-medium mb-3 ${darkMode ? "text-white" : "text-gray-900"}`}>
                    Associar contatos a um grupo
                  </label>

                  <div className="space-y-3">
                    {/* Opção: Nenhum grupo */}
                    <label className={`flex items-start p-3 rounded-md cursor-pointer transition-colors ${
                      selectedGroupOption === 'none'
                        ? darkMode ? "bg-green-900/30 border-2 border-nomoz-orange" : "bg-green-50 border-2 border-nomoz-orange"
                        : darkMode ? "bg-zinc-700 border border-gray-600 hover:bg-zinc-600" : "bg-gray-50 border border-gray-300 hover:bg-gray-100"
                    }`}>
                      <input
                        type="radio"
                        name="groupOption"
                        value="none"
                        checked={selectedGroupOption === 'none'}
                        onChange={(e) => setSelectedGroupOption(e.target.value)}
                        className="mt-0.5 h-4 w-4 text-green-600 focus:ring-nomoz-orange"
                      />
                      <div className="ml-3">
                        <span className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                          Não associar a nenhum grupo
                        </span>
                        <p className={`text-xs mt-1 ${darkMode ? "text-gray-400" : "text-gray-600"}`}>
                          Os contatos serão apenas importados
                        </p>
                      </div>
                    </label>

                    {/* Opção: Grupo existente */}
                    <label className={`flex items-start p-3 rounded-md cursor-pointer transition-colors ${
                      selectedGroupOption === 'existing'
                        ? darkMode ? "bg-green-900/30 border-2 border-nomoz-orange" : "bg-green-50 border-2 border-nomoz-orange"
                        : darkMode ? "bg-zinc-700 border border-gray-600 hover:bg-zinc-600" : "bg-gray-50 border border-gray-300 hover:bg-gray-100"
                    }`}>
                      <input
                        type="radio"
                        name="groupOption"
                        value="existing"
                        checked={selectedGroupOption === 'existing'}
                        onChange={(e) => setSelectedGroupOption(e.target.value)}
                        className="mt-0.5 h-4 w-4 text-green-600 focus:ring-nomoz-orange"
                      />
                      <div className="ml-3 flex-1">
                        <span className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                          Adicionar a um grupo existente
                        </span>
                        {selectedGroupOption === 'existing' && (
                          <select
                            value={selectedGroupId}
                            onChange={(e) => setSelectedGroupId(e.target.value)}
                            className={`mt-2 block w-full rounded-md border py-2 px-3 text-sm ${
                              darkMode
                                ? "border-gray-600 bg-zinc-600 text-white"
                                : "border-gray-300 bg-white text-gray-900"
                            }`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <option value="">Selecione um grupo...</option>
                            {groups.map((group) => (
                              <option key={group.id} value={group.id}>
                                {group.nome}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    </label>

                    {/* Opção: Criar novo grupo */}
                    <label className={`flex items-start p-3 rounded-md cursor-pointer transition-colors ${
                      selectedGroupOption === 'new'
                        ? darkMode ? "bg-green-900/30 border-2 border-nomoz-orange" : "bg-green-50 border-2 border-nomoz-orange"
                        : darkMode ? "bg-zinc-700 border border-gray-600 hover:bg-zinc-600" : "bg-gray-50 border border-gray-300 hover:bg-gray-100"
                    }`}>
                      <input
                        type="radio"
                        name="groupOption"
                        value="new"
                        checked={selectedGroupOption === 'new'}
                        onChange={(e) => setSelectedGroupOption(e.target.value)}
                        className="mt-0.5 h-4 w-4 text-green-600 focus:ring-nomoz-orange"
                      />
                      <div className="ml-3 flex-1">
                        <span className={`text-sm font-medium ${darkMode ? "text-white" : "text-gray-900"}`}>
                          Criar novo grupo
                        </span>
                        {selectedGroupOption === 'new' && (
                          <div className="mt-2 space-y-2" onClick={(e) => e.stopPropagation()}>
                            <input
                              type="text"
                              placeholder="Nome do grupo"
                              value={newGroupName}
                              onChange={(e) => setNewGroupName(e.target.value)}
                              className={`block w-full rounded-md border py-2 px-3 text-sm ${
                                darkMode
                                  ? "border-gray-600 bg-zinc-600 text-white placeholder-gray-400"
                                  : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                              }`}
                            />
                            <input
                              type="text"
                              placeholder="Descrição (opcional)"
                              value={newGroupDescription}
                              onChange={(e) => setNewGroupDescription(e.target.value)}
                              className={`block w-full rounded-md border py-2 px-3 text-sm ${
                                darkMode
                                  ? "border-gray-600 bg-zinc-600 text-white placeholder-gray-400"
                                  : "border-gray-300 bg-white text-gray-900 placeholder-gray-500"
                              }`}
                            />
                          </div>
                        )}
                      </div>
                    </label>
                  </div>
                </div>

                <div className={`p-4 rounded-md ${darkMode ? "bg-zinc-700" : "bg-blue-50"}`}>
                  <div className="flex items-center justify-between mb-2">
                    <h4 className={`text-sm font-medium ${darkMode ? "text-blue-400" : "text-blue-800"}`}>
                      Formato esperado:
                    </h4>
                    <button
                      type="button"
                      onClick={downloadTemplate}
                      className={`text-xs px-2 py-1 rounded ${darkMode ? "bg-blue-600 hover:bg-blue-700 text-white" : "bg-blue-600 hover:bg-blue-700 text-white"}`}
                    >
                      Baixar Modelo
                    </button>
                  </div>
                  <div className={`text-xs ${darkMode ? "text-gray-300" : "text-gray-600"}`}>
                    <p>• Primeira linha: cabeçalhos (nome, telefone)</p>
                    <p>• Exemplo: nome,telefone</p>
                    <p>• Exemplo: João Silva,11999999999</p>
                  </div>
                </div>
              </div>

              {/* Consentimento */}
              <label className={`flex items-start gap-3 p-3 rounded-md border cursor-pointer ${darkMode ? 'border-gray-600 bg-zinc-700' : 'border-gray-200 bg-gray-50'}`}>
                <input type="checkbox" checked={consentimentoDeclarado} onChange={e => setConsentimentoDeclarado(e.target.checked)}
                  className="mt-0.5 h-4 w-4 rounded text-nomoz-orange focus:ring-nomoz-orange" />
                <span className={`text-xs ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                  Declaro que possuo consentimento prévio de todos os contatos para receber comunicações.
                </span>
              </label>

              <div className="modal-footer">
                <button
                  type="button"
                  onClick={() => {
                    setShowImportModal(false);
                    setCsvFile(null);
                    setSelectedGroupOption('none');
                    setSelectedGroupId('');
                    setNewGroupName('');
                    setNewGroupDescription('');
                  }}
                  className={`modal-button modal-button-secondary ${
                    darkMode
                      ? "border-gray-600 text-white hover:bg-zinc-700"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleImportCSV}
                  disabled={!csvFile || importing}
                  className="modal-button gradient-button disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {importing ? "Importando..." : "Importar"}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </Layout>
  );
};

export default Contacts;